import {
  isManualResultStale,
  specLabel,
  type StoryCheckColumn,
  type StoryCheckStatus,
  type StoryListItem,
  type StoryMaturity,
  type ViolationTally,
} from '../../lib/stories';

/**
 * 유저 스토리 모니터의 **화면 파생 로직** — 컴포넌트에서 분리해 둔 순수 함수.
 *
 * 설계 정본: `docs/decisions/2026-10-07-prod-story-verification-monitor.md`.
 * jsdom 없이 재지는 축(라벨·분모·항등식)을 여기서 소유하고 짝 spec이 그것을 단언한다 —
 * admin은 Vite(esbuild) 빌드라 **CI가 타입을 보지 않으므로** 이 층의 검사가 유일한 방어선이다.
 *
 * ## 🔴 여기서 «하지 않는» 것 — 숙성(`auto`/`manual`/`none`) 재파생
 * 숙성은 `guard` **소스 스캔** 파생이고(불변식·통합·E2E 선언을 스캔한다) 서버는 레포를 스캔하지
 * 않는다. run이 그 시점 요약을 `story_check_runs.registry_summary`에 기준과 함께 박지만 **그 표를
 * 읽는 엔드포인트가 없다**(레포 전수: `registrySummary`를 쓰는 서버 코드는 `create` 두 자리뿐).
 * ⇒ 관측(= 불변식 결과가 있나)으로 `auto`를 흉내내면 `story:check`의 파생과 **조용히 어긋난다**.
 * 결정 기록이 「검사가 지워졌는데 DB는 `auto`라고 말하는 가장 위험한 낡음」이라 적은 그 축이라,
 * 화면은 **커버리지 칸을 비워 두고 왜 비었는지 적는다**. 추정치로 채우지 말 것.
 */

/** 상태 배지 색 — 도메인 상태 → tone 매핑은 소비처 책임(`Badge`의 tone은 색 이름이다). */
export const STATUS_TONE: Record<StoryCheckStatus, 'emerald' | 'rose' | 'amber' | 'orange'> = {
  PASS: 'emerald',
  VIOLATION: 'rose',
  VACUOUS: 'amber',
  ERROR: 'orange',
};

/**
 * 상태 라벨. **`null`은 「판정 없음」이고 「통과」가 아니다** — 섞으면 아무것도 안 잰 것이 초록으로
 * 보인다(이 화면이 막으려는 바로 그 오독이다).
 */
export function statusLabel(status: StoryCheckStatus | null): string {
  switch (status) {
    case 'PASS':
      return '통과';
    case 'VIOLATION':
      return '위반';
    case 'VACUOUS':
      return '공허';
    case 'ERROR':
      return '측정 실패';
    case null:
      return '판정 없음';
    default: {
      // tsc가 여기서 멈춘다 — 서버 enum에 멤버가 늘면 이 함수와 `STATUS_TONE`을 함께 고쳐야 한다
      const unhandled: never = status;
      return String(unhandled);
    }
  }
}

/** 상태 한 줄 설명 — 「공허」·「측정 실패」가 왜 초록이 아닌지를 그 자리에서 읽게 한다. */
export const STATUS_HINTS: Record<StoryCheckStatus | 'null', string> = {
  PASS: '위반 열 전부 0 ∧ 대조군 열 전부 ≥ 1',
  VIOLATION: '위반 열 중 하나라도 ≥ 1 — 관측된 사실이다',
  VACUOUS: '위반 열은 0인데 **대조군이 0** ⇒ 그 0은 증거가 아니다',
  ERROR: '잴 수 없었다 — 단일 행이 아니다 · 별칭이 없다 · 숫자가 아니다',
  null: '결과 0건 — 아무도 재지 않았다. 통과가 아니다',
};

/** 측정 환경 배지 — dev 판정이 prod 판정처럼 읽히지 않게 색을 가른다. */
export const ENV_TONE: Record<string, 'violet' | 'blue' | 'slate'> = {
  prod: 'violet',
  dev: 'blue',
};

/**
 * 위반 네 칸의 **항등식** `open === openUnacknowledged + openAcknowledged`.
 *
 * 서버 유닛·통합이 이것을 단언하지만 **화면도 센다** — 응답 «안»의 두 수를 대조하는 것이
 * API만으로도 SQL만으로도 못 잡는 종류를 잡는 자리이고(선례: ML 화면의 `countsDisagree`),
 * 어긋나면 그 수를 그냥 그리는 대신 **어긋났다고 말해야** 한다.
 */
export function violationTallyDisagrees(tally: ViolationTally): boolean {
  return tally.open !== tally.openUnacknowledged + tally.openAcknowledged;
}

/**
 * 판정 열 전문(`columns`) 방어적 파싱 — 서버가 `Json`으로 내려 **형상이 보장되지 않는다**.
 *
 * 파싱 실패를 빈 배열로 만들지 않는다(`null`) — 「열이 없다」와 「형상이 낯설다」는 다른 사실이고,
 * 빈 배열로 접으면 화면이 「잴 열이 없었다」로 **거짓 안내**를 한다.
 */
export function parseCheckColumns(columns: unknown): StoryCheckColumn[] | null {
  if (!Array.isArray(columns)) return null;
  const parsed: StoryCheckColumn[] = [];
  for (const raw of columns) {
    if (typeof raw !== 'object' || raw === null) return null;
    const row = raw as Record<string, unknown>;
    if (typeof row.col !== 'string' || typeof row.must !== 'string') return null;
    parsed.push({
      col: row.col,
      must: row.must,
      unit: typeof row.unit === 'string' ? row.unit : null,
      why: typeof row.why === 'string' ? row.why : null,
      value: typeof row.value === 'number' ? row.value : null,
      ok: typeof row.ok === 'boolean' ? row.ok : null,
    });
  }
  return parsed;
}

/**
 * run이 투과한 레지스트리 요약 — **숙성 분포와 커버리지 두 비율이 여기서만 온다.**
 *
 * 형상 소유자는 **러너**(`scripts/stories/check.mjs`의 `summarize`)이고 서버는 검증 없이 투과한다.
 * ⚠️ **분모는 `queueTotal`(= `total − maturity.offQueue`)이고 `total`이 아니다** — `offQueue`는 잴
 * 기대가 없어 분모에 두면 커버리지가 영구히 낮게 보인다. 그래서 러너 보고가 **두 수를 함께 적고**
 * (그 파일 주석이 「보고가 두 수를 함께 적는다」라 명시) 이 화면도 그렇게 그린다.
 * ⚠️ `offQueueDeclared`와 `maturity.offQueue`는 **다른 수**다 — 전자는 선언 전체이고 후자는 그중
 * 보장 수단이 없어 큐에서 빠진 것이다. 차(`offQueueStillMeasured`)는 **재판정 큐에 그대로 있다.**
 */
export interface RegistrySummary {
  total: number;
  /** 커버리지 분모 — `total − maturity.offQueue`. 그 항등식은 `queueTotalDisagrees`가 센다 */
  queueTotal: number;
  /**
   * 숙성 분포 — **키 집합을 `StoryMaturity`에서 파생시킨다**(손으로 네 칸을 적지 않는다).
   *
   * 그 선언의 JSDoc이 이 자리를 예고해 뒀다 — 「생산자가 **untyped `.mjs`**라 tsc가 그 어긋남을
   * 원리적으로 못 본다 … 3단계에서 admin 화면이 이 타입을 쓰면 그때도 못 잡아 **화면이 그 칸을
   * 조용히 떨어뜨린다**」. 그래서 `Record`로 받는다: 러너가 숙성 값을 하나 더 내면 **tsc가 여기와
   * `parseRegistrySummary`를 함께 멈춘다**(종전 손나열은 `offQueue`가 늦게 들어온 그 사고를 못 봤다).
   */
  maturity: Record<StoryMaturity, number>;
  /** `auto + manual` / `queueTotal`. **`queueTotal === 0`이면 null** */
  coverage: number | null;
  /** `auto` / `queueTotal` ← 숙성도. **`queueTotal === 0`이면 null** */
  autoCoverage: number | null;
  /** 선언 전체(= `manual`로 집계되는 것까지). 러너가 싣지 않으면 null */
  offQueueDeclared: number | null;
  /** 선언했는데 보장 수단이 있어 큐에 남은 것. 러너가 싣지 않으면 null */
  offQueueStillMeasured: number | null;
  /** 사유 분류별 건수. 러너가 싣지 않으면 null */
  offQueueByKind: Record<string, number> | null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * `registrySummary` 방어적 파싱 — **서버가 검증하지 않으므로 화면이 「키 부재」에 견뎌야 한다.**
 *
 * `null`을 돌려주는 것이 곧 「견딤」이다(빈 분포를 꾸미지 않는다 — 0으로 메우면 **아무것도 안 재는
 * 것이 완벽으로 보이는** 그 함정을 화면이 스스로 만든다).
 *
 * ## 요구하는 것과 견디는 것
 * - **요구**: `total`·`queueTotal`·`maturity`의 네 수 — 이것들이 없으면 분포도 분모도 그릴 수 없다.
 * - **견딤**: `coverage`·`autoCoverage`(없으면 null) · `offQueue*` 상세(없으면 null).
 *
 * ⚠️ `coverage === null`의 **사유는 이 함수가 정하지 않는다** — 「큐 분모가 0이다」와 「러너가 그 칸을
 * 싣지 않았다」는 다른 사실이고, 소비처가 `queueTotal === 0`으로 그 둘을 가른다(`coverageAbsenceReason`).
 */
export function parseRegistrySummary(raw: unknown): RegistrySummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;

  const total = numberOrNull(row.total);
  const queueTotal = numberOrNull(row.queueTotal);
  if (total === null || queueTotal === null) return null;

  if (typeof row.maturity !== 'object' || row.maturity === null) return null;
  const rawMaturity = row.maturity as Record<string, unknown>;
  const auto = numberOrNull(rawMaturity.auto);
  const manual = numberOrNull(rawMaturity.manual);
  const none = numberOrNull(rawMaturity.none);
  const offQueue = numberOrNull(rawMaturity.offQueue);
  if (auto === null || manual === null || none === null || offQueue === null) return null;

  let offQueueByKind: Record<string, number> | null = null;
  if (typeof row.offQueueByKind === 'object' && row.offQueueByKind !== null) {
    const entries = Object.entries(row.offQueueByKind as Record<string, unknown>).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    );
    offQueueByKind = Object.fromEntries(entries);
  }

  return {
    total,
    queueTotal,
    maturity: { auto, manual, none, offQueue },
    coverage: numberOrNull(row.coverage),
    autoCoverage: numberOrNull(row.autoCoverage),
    offQueueDeclared: numberOrNull(row.offQueueDeclared),
    offQueueStillMeasured: numberOrNull(row.offQueueStillMeasured),
    offQueueByKind,
  };
}

/**
 * 커버리지 **분모의 항등식** `queueTotal === total − maturity.offQueue`.
 *
 * 형제는 `violationTallyDisagrees`이고 **같은 이유로 여기 있다** — 화면이 그 산수를 글로 인쇄하기
 * 때문이다(`RecentRunsPanel`의 분모 칸 hint가 「전체 N − 큐 밖 M」을 적는다). 인쇄하면서 대조하지
 * 않으면 세 수가 어긋나는 날 **화면이 맞지 않는 산수를 보여주고 커버리지 분모가 조용히 틀린다.**
 *
 * ⚠️ 이 축은 **서버가 재지 않는다** — 형상 소유자는 러너(`scripts/stories/check.mjs`의 `summarize`)이고
 * 서버는 검증 없이 투과한다(팀장 확정 계약 2026-10-13). 그래서 **응답 안의 세 수를 대조하는 이 자리가
 * 유일한 탐지기**다(선례: ML 화면의 `countsDisagree`).
 * ⚠️ `total`과 `queueTotal`을 **비율로 재지 않는다** — 분모가 작아지는 것은 정상이고(그게 `offQueue`의
 * 존재 이유다) 잡아야 하는 것은 **등식의 파괴**다.
 */
export function queueTotalDisagrees(summary: RegistrySummary): boolean {
  return summary.queueTotal !== summary.total - summary.maturity.offQueue;
}

/**
 * 비율이 없는 **사유** — 두 사실을 가른다. 한 문구로 뭉치면 둘 중 하나가 숨는다.
 *
 * `'queue-empty'`: 큐 분모가 0이라 비율이 **성립하지 않는다**(러너도 그때 `null`을 낸다).
 * `'not-reported'`: 분모는 있는데 러너가 그 칸을 **싣지 않았다** — 형상이 바뀠다는 신호다.
 */
export function coverageAbsenceReason(
  summary: RegistrySummary,
): 'queue-empty' | 'not-reported' | null {
  if (summary.coverage !== null && summary.autoCoverage !== null) return null;
  return summary.queueTotal === 0 ? 'queue-empty' : 'not-reported';
}

/**
 * run의 **범위** — ⚠️ **빈 배열 = 전수**다.
 *
 * 비어 있지 않으면 부분 run이고, 그 사실을 숫자 옆에 적지 않으면 **부분 run의 「위반 0」이 전수로
 * 읽힌다**(스키마가 `spec_filter` 칸에 바로 그 경고를 달고 있다).
 */
export function runScopeLabel(specFilter: string[]): string {
  return specFilter.length === 0 ? '전수' : `부분 run (${specFilter.length}개 명세)`;
}

/**
 * 활성 필터 한 축 — **「무엇을 좁혔나」와 「어떻게 좁혔나」를 함께 든다.**
 *
 * `effect`가 성질이고 셋이 **서로 다른 일을 한다**(한 통에 세면 라벨이 거짓이 된다):
 * - `'subset'`: 스토리 **집합 자체**가 줄었다(`specKey`·`status`). ⇒ 요약의 모든 수가 부분집합의
 *   집계다. 🔴 상태 = `PASS`를 고르면 「건강도 100% · 공허·실패·미판정 0」이 뜨는데, 그것이 바로
 *   이 화면이 막으려는 오독(**아무것도 안 재는 것이 완벽으로 보인다**)이다.
 * - `'basis'`: 집합은 같고 **판정 기준**이 좁혀졌다(`env`). 서버가 `loadLatestResults({ env })`로
 *   접기만 가르므로 `total`은 그대로인데 **분포가 바뀐다**(dev에서만 재진 스토리가 「판정 없음」이 된다).
 * - `'widen'`: 범위가 **넓어졌다**(`includeRetired`) — 전수보다 큰 집합이라 「전수」 금지 대상이 아니다.
 */
export interface StoryScopeAxis {
  /** 화면 라벨(「명세」·「상태」·「측정 환경」·「은퇴 스토리」) */
  axis: string;
  /** 사람이 읽는 값(명세는 이름으로, 상태는 라벨로 — 키를 그대로 보여주지 않는다) */
  value: string;
  effect: 'subset' | 'basis' | 'widen';
}

/**
 * 요약 카드가 그리는 수의 **범위** — 「이 수가 무엇에 대한 것인가」.
 *
 * ⚠️ **큐 필터는 여기 없다.** 그것은 클라이언트에서 목록만 접으므로 `counts`·`total`에 영향이 **0**이다
 * (서버 질의 키에도 안 들어간다). 넣으면 요약이 바뀌지 않았는데 「필터 적용 중」이 뜬다.
 */
export interface StoryScope {
  axes: StoryScopeAxis[];
  /**
   * 집합을 좁히는 축이 하나라도 있나 — **「전수」 라벨을 쓸 수 있는지의 그 조건.**
   * 수와 라벨을 **이 하나에서** 파생시킨다(`scopedCountCaption`) — 따로 쓰면 한쪽만 고쳐져
   * 라벨이 거짓이 되고, 그 거짓은 **아무 검사도 깨뜨리지 않는다**(숫자는 맞고 라벨만 틀리다).
   */
  subset: boolean;
  /**
   * 배너가 **어느 문구를 쓸지** — `null`이면 배너 없음(축 0개).
   *
   * ⚠️ 이 칸이 있는 이유: 소비처가 `subset`만 보고 「아니면 env 이야기」로 갈라 쓰면 **은퇴 포함만
   * 걸린 경우에 env 문구가 뜬다**(틀린 안내다 — 그리고 숫자는 맞으니 아무 검사도 깨지지 않는다).
   * 그래서 문구 갈래를 **여기서 한 번** 파생시킨다. 우선순위는 집합 축 > 기준 축 > 넓힘 축이다
   * (가장 센 오독을 먼저 말한다).
   */
  notice: 'subset' | 'basis' | 'widen' | null;
}

export interface StoryScopeFilters {
  /** `''`이면 전 env */
  env: string;
  specKey: string;
  status: string;
  includeRetired: boolean;
}

/**
 * 활성 필터 → 범위 서술. **서버가 `counts: buildCounts(filtered)`·`total: filtered.length`를 내므로**
 * (`admin-stories.service.ts`의 `listStories`) 요약 카드의 수는 **언제나 이 범위의 수**다.
 */
export function describeStoryScope(filters: StoryScopeFilters): StoryScope {
  const axes: StoryScopeAxis[] = [];
  if (filters.specKey !== '') {
    axes.push({ axis: '명세', value: specLabel(filters.specKey), effect: 'subset' });
  }
  if (filters.status !== '') {
    axes.push({
      axis: '상태',
      value: statusLabel(filters.status as StoryCheckStatus),
      effect: 'subset',
    });
  }
  if (filters.env !== '') {
    axes.push({ axis: '측정 환경', value: filters.env, effect: 'basis' });
  }
  if (filters.includeRetired) {
    axes.push({ axis: '은퇴 스토리', value: '포함', effect: 'widen' });
  }

  const subset = axes.some((axis) => axis.effect === 'subset');
  const basis = axes.some((axis) => axis.effect === 'basis');
  const notice = axes.length === 0 ? null : subset ? 'subset' : basis ? 'basis' : 'widen';
  return { axes, subset, notice };
}

/**
 * 요약 카드의 스토리 수 캡션 — **수와 범위 라벨을 한 함수에서 만든다.**
 *
 * 둘을 따로 쓰면 숫자는 필터를 따르고 라벨은 「전수」에 머무는 상태가 되는데, 그때 **tsc도 spec도
 * 못 잡는다**(숫자가 맞고 라벨만 거짓이다). 그래서 이 함수가 그 둘의 **단일 파생점**이다.
 */
export function scopedCountCaption(total: number, scope: StoryScope): string {
  return scope.subset ? `필터 범위 ${total}건` : `전수 ${total}건`;
}

/** 명세 드롭다운 한 칸. */
export interface SpecFilterOption {
  specKey: string;
  label: string;
}

/**
 * 명세 드롭다운 옵션 — **정적 레지스트리 키 ∪ 응답에 있는 키 ∪ 현재 선택.**
 *
 * ⚠️ **응답에서만 파생시키면 필터가 자기 선택지를 먹는다** — 명세 A를 고르면 응답이 A만 담아 목록이
 * A 하나로 줄고, B로 가려면 「전체」로 되돌려야 한다(상태 필터까지 걸면 위반 없는 명세가 사라진다).
 *
 * 합집합의 세 조각이 각자 다른 일을 한다:
 * - **레지스트리 키**: 평소의 전수. 선언 순서를 지킨다(사람이 기대하는 순서 = id 블록 순서).
 * - **응답 키**: DB 미러가 코드보다 앞선 명세(은퇴 등)도 **고를 수 있게**. 라벨은 키로 떨어진다.
 * - **현재 선택**: 🔑 **선택된 값은 언제나 옵션에 있어야 한다** — 없으면 `<select>`가 빈 칸으로
 *   보여 「전체」와 구별되지 않는다(사용자는 필터가 풀렸다고 읽는다).
 */
export function specFilterOptions(args: {
  registryKeys: string[];
  storyKeys: string[];
  /** `''`(전체)이면 더하지 않는다 */
  selected: string;
}): SpecFilterOption[] {
  const ordered: string[] = [...args.registryKeys];
  const seen = new Set(ordered);
  // 레지스트리 밖 키는 뒤에 사전순으로 — 순서가 run마다 흔들리지 않게
  const extras = [...new Set([...args.storyKeys, args.selected])]
    .filter((key) => key !== '' && !seen.has(key))
    .sort((a, b) => a.localeCompare(b));
  ordered.push(...extras);
  return ordered.map((specKey) => ({ specKey, label: specLabel(specKey) }));
}

/** 명세 한 줄 — 「명세별」 표의 행. */
export interface SpecSummary {
  specKey: string;
  label: string;
  /**
   * **받은 `stories` 중** 그 명세의 수.
   * ⚠️ 「그 명세의 스토리 수」가 아니다 — 상태 필터·`limit`·은퇴 제외가 걸리면 그 범위의 수다
   * (상태 = `PASS`면 이 수가 곧 통과 수가 되어 표가 전부 100%로 보인다). 그래서 표 머리가
   * `describeStoryScope`의 범위를 함께 싣는다.
   */
  total: number;
  pass: number;
  violation: number;
  vacuous: number;
  error: number;
  /** 결과 0건 — 「아무도 안 쟀다」 */
  unjudged: number;
  /** 건강도 분모는 `pass + violation`뿐이다 */
  healthDenominator: number;
  /** 분모가 0이면 **null** — 0으로 나눈 100%를 만들지 않는다 */
  healthRatio: number | null;
  /**
   * `verify` 절차가 선언된 스토리 수(`verifyKind != null`).
   * ⚠️ **커버리지가 아니다** — 숙성은 `guard` 소스 파생이라 이 수로 `auto`/`manual`을 가를 수 없다
   * (한 스토리가 불변식과 `verify`를 **둘 다** 가질 수 있다). 라벨을 「커버리지」라 쓰지 말 것.
   */
  verifyDeclared: number;
  /** 미해소 위반 건수 합 */
  openViolations: number;
  /** 그중 아직 확인 처리되지 않은 것 — 🔴 큐 */
  openUnacknowledged: number;
  /** 사람 판정에 기대고 있는데 낡은 스토리 수 — ⚠️ (경과일 대리 신호) */
  manualStale: number;
}

/**
 * 명세별 집계 — **정렬은 스토리 수 내림차순, 같으면 키 사전순**(목록이 run마다 흔들리지 않게).
 *
 * ⚠️ 서버 `counts`를 쪼개 쓰지 않는다. 그쪽은 **필터 적용 후 전체**의 집계라 명세별로 가를 수 없고,
 * 화면이 명세별로 또 질의하면 run이 그 사이에 돌아 **행마다 기준이 달라진다**(「수에는 기준이
 * 붙어야 한다」). 그래서 **한 응답의 `stories`를 그 자리에서 접는다.**
 */
export function summarizeBySpec(stories: StoryListItem[]): SpecSummary[] {
  const bySpec = new Map<string, StoryListItem[]>();
  for (const story of stories) {
    const bucket = bySpec.get(story.specKey);
    if (bucket) bucket.push(story);
    else bySpec.set(story.specKey, [story]);
  }

  const rows = [...bySpec.entries()].map(([specKey, items]) => {
    let pass = 0;
    let violation = 0;
    let vacuous = 0;
    let error = 0;
    let unjudged = 0;
    let verifyDeclared = 0;
    let openViolations = 0;
    let openUnacknowledged = 0;
    let manualStale = 0;

    for (const story of items) {
      switch (story.status) {
        case 'PASS':
          pass += 1;
          break;
        case 'VIOLATION':
          violation += 1;
          break;
        case 'VACUOUS':
          vacuous += 1;
          break;
        case 'ERROR':
          error += 1;
          break;
        case null:
          unjudged += 1;
          break;
        default: {
          const unhandled: never = story.status;
          void unhandled;
          error += 1;
          break;
        }
      }
      if (story.verifyKind !== null) verifyDeclared += 1;
      openViolations += story.violations.open;
      openUnacknowledged += story.violations.openUnacknowledged;
      if (isManualResultStale(story)) manualStale += 1;
    }

    const healthDenominator = pass + violation;
    return {
      specKey,
      label: specLabel(specKey),
      total: items.length,
      pass,
      violation,
      vacuous,
      error,
      unjudged,
      healthDenominator,
      healthRatio: healthDenominator === 0 ? null : pass / healthDenominator,
      verifyDeclared,
      openViolations,
      openUnacknowledged,
      manualStale,
    };
  });

  return rows.sort((a, b) => b.total - a.total || a.specKey.localeCompare(b.specKey));
}

/** 상세의 「위반 건」 제목 — 마커·수·목록 주석. */
export interface ViolationHeading {
  /** `'🔴'` 또는 `''` */
  marker: string;
  /** **미해소 수**(`tally.open`) — 목록 길이가 아니다 */
  count: number;
  /** 목록이 그 수와 다를 때만 — 「목록 N건(해소 포함)」. 같으면 `null` */
  listNote: string | null;
}

/**
 * 상세 제목의 마커와 수를 **한 축에서** 파생시킨다.
 *
 * 🔴 종전엔 두 축이 한 제목에 섞여 있었다 — `위반 건 {open > 0 ? '🔴' : ''} {violations.length}`에서
 * **마커는 미해소 수**에서 오고 **숫자는 목록 길이**(해소분 포함 · 서버 상한에 잘린다)에서 왔다.
 * 그래서 해소만 7건인 스토리가 **「위반 건 7」(🔴 없음)**으로 보였다 — 7건이 아직 있는 것처럼 읽히고,
 * 반대로 잘린 목록에서는 미해소가 있는데 수가 작아 보인다.
 *
 * ⇒ 제목은 **미해소 하나**로 말하고 목록 길이는 **보조 칸**으로 가른다. 둘을 같은 함수가 만들므로
 * 마커와 수가 다른 축을 가리킬 수 없다.
 * ⚠️ 목록 길이가 작은 것은 **두 사유**가 있다(해소분 포함 ↔ 서버 상한 절단) — 후자의 경고는 이미
 * 그 카드가 따로 띄우므로(집계 > 목록) 여기서는 뭉치지 않고 「해소 포함」만 적는다.
 */
export function violationHeading(tally: ViolationTally, listLength: number): ViolationHeading {
  return {
    marker: tally.open > 0 ? '🔴' : '',
    count: tally.open,
    listNote: listLength === tally.open ? null : `목록 ${listLength}건 (해소 포함)`,
  };
}

/** 목록 전체에서 센 ⚠️ 큐 — 사람 판정에 기대고 있는데 낡은 것. */
export function countManualStale(stories: StoryListItem[]): number {
  return stories.reduce((sum, story) => sum + (isManualResultStale(story) ? 1 : 0), 0);
}

/** `verify` 절차가 선언된 스토리 수. ⚠️ 커버리지가 아니다(위 `SpecSummary.verifyDeclared` 주석). */
export function countVerifyDeclared(stories: StoryListItem[]): number {
  return stories.reduce((sum, story) => sum + (story.verifyKind !== null ? 1 : 0), 0);
}

/** 비율 표시 — 분모가 0이면 **`—`**다. 「100%」를 만들지 않는다. */
export function formatRatio(ratio: number | null): string {
  if (ratio === null) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** 상대 시각. null이면 `—`(「방금」이 아니다 — 판정이 없는 것과 섞인다). */
export function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

/** `US-0013 (사진 P13)` — 기존 보고서·저널의 「P13」을 추적할 수 있어야 한다(설계 요구). */
export function storyIdLabel(story: { id: string; specKey: string; localNo: string }): string {
  return `${story.id} (${specLabel(story.specKey)} ${story.localNo})`;
}
